import {
  ASTER_CHARACTER_COMBAT_PROMPT,
  CAMELLIA_QUEST_OFFER_PROMPT,
  CAMELLIA_SEMANTIC_QUEST_JUDGE_PROMPT,
  DAHLIA_SITE_STRUCTURE_PROMPT,
  FREESIA_PROFILE_PROMPT,
  GARDEN_CORE_PROMPT,
  HEATHER_SESSION_SUMMARY_PROMPT,
  HEATHER_STAGE_FUSION_PROMPT,
  HEATHER_STAGE_SUMMARY_PROMPT,
  HYACINTH_PROFILE_PROMPT,
  IRIS_CHARACTER_BLUEPRINT_PROMPT,
  IVY_TOOL_CALL_PROTOCOL_PROMPT,
  LILY_CHARACTER_PRESENCE_PROMPT,
  VERBENA_SITE_CONTENT_PROMPT,
} from './prompts';
import type { LLMMessage } from '@/IsekaiRPG/modules/llm/llmTypes';

export type GardenFlowerId =
  | 'aster'
  | 'camellia'
  | 'dahlia'
  | 'freesia'
  | 'heather'
  | 'hyacinth'
  | 'iris'
  | 'ivy'
  | 'lily'
  | 'verbena';

export type GardenFlowerTask =
  | 'character_blueprint_design'
  | 'character_combat_design'
  | 'quest_offer_generation'
  | 'semantic_quest_judgement'
  | 'character_conversation'
  | 'character_presence_design'
  | 'site_shell_generation'
  | 'site_rooms_generation'
  | 'site_content_generation'
  | 'story_history_session_summary'
  | 'story_history_stage_fusion'
  | 'story_history_stage_summary'
  | 'battle_narration'
  | 'chat_interaction_protocol';

export type GardenPromptBlockKey =
  | 'aster_character_combat'
  | 'camellia_quest_offer'
  | 'camellia_semantic_quest_judge'
  | 'dahlia_site_structure'
  | 'garden_core'
  | 'freesia_profile'
  | 'heather_session_summary'
  | 'heather_stage_fusion'
  | 'heather_stage_summary'
  | 'hyacinth_profile'
  | 'iris_character_blueprint'
  | 'ivy_protocol'
  | 'lily_character_presence'
  | 'verbena_site_content';

export type GardenCorePromptMode = 'preset_marker' | 'inline_with_core' | 'none';
export type GardenFlowerContextStrategy =
  | 'shared_conversation_runtime'
  | 'dedicated_generator_runtime'
  | 'history_summary_runtime';

export interface GardenPromptBlockProfile {
  key: GardenPromptBlockKey;
  label: string;
  marker: string;
  content: string;
  description: string;
}

export interface GardenFlowerPromptBlock {
  key: GardenPromptBlockKey;
  label: string;
  marker: string;
  content: string;
}

export interface GardenFlowerTaskProfile {
  task: GardenFlowerTask;
  flowerId: GardenFlowerId;
  flowerName: string;
  profileName: string;
  responsibility: string;
  contextStrategy: GardenFlowerContextStrategy;
  blockKeys: readonly GardenPromptBlockKey[];
  detectMarkers: readonly string[];
  isPrimaryFlower: boolean;
  markerIdentifier: string | null;
  promptRole: LLMMessage['role'];
  corePromptMode: GardenCorePromptMode;
  allowedCollaborationTargets: readonly GardenFlowerId[];
}

const GARDEN_PROMPT_BLOCK_PROFILES: Record<GardenPromptBlockKey, GardenPromptBlockProfile> = {
  aster_character_combat: {
    key: 'aster_character_combat',
    label: 'Aster Character Combat Profile',
    marker: '<Character_Combat_Design_Rules>',
    content: ASTER_CHARACTER_COMBAT_PROMPT,
    description: '紫苑的战斗设计规则，负责把完整人物设定翻译成属性偏态与技能表达。',
  },
  camellia_quest_offer: {
    key: 'camellia_quest_offer',
    label: 'Camellia Quest Offer Profile',
    marker: '<Quest_Offer_Generation_Task>',
    content: CAMELLIA_QUEST_OFFER_PROMPT,
    description: '山茶的对话任务提议生成规则，负责把当前对话转成结构化任务模板。',
  },
  camellia_semantic_quest_judge: {
    key: 'camellia_semantic_quest_judge',
    label: 'Camellia Semantic Quest Judge Profile',
    marker: '<Semantic_Quest_Judgement_Task>',
    content: CAMELLIA_SEMANTIC_QUEST_JUDGE_PROMPT,
    description: '山茶的语义任务裁定规则，负责根据完整剧情历史判断任务是否完成或失败。',
  },
  dahlia_site_structure: {
    key: 'dahlia_site_structure',
    label: 'Dahlia Site Structure Profile',
    marker: '<Site_Architecture_Generation_Task>',
    content: DAHLIA_SITE_STRUCTURE_PROMPT,
    description: '桔梗的据点架构规则，负责据点本体、房间蓝图与探索节奏设计。',
  },
  garden_core: {
    key: 'garden_core',
    label: 'Garden Core',
    marker: '<Garden_Core>',
    content: GARDEN_CORE_PROMPT,
    description: '花园体系的共享总规则层，不枚举具体花职责。',
  },
  freesia_profile: {
    key: 'freesia_profile',
    label: 'Freesia Prompt Profile',
    marker: '<Conversation_Rules>',
    content: FREESIA_PROFILE_PROMPT,
    description: '小苍兰的人物会话规则，负责角色对话与局部场景叙事。',
  },
  heather_session_summary: {
    key: 'heather_session_summary',
    label: 'Heather Session Summary Profile',
    marker: '<Session_Summary_Task>',
    content: HEATHER_SESSION_SUMMARY_PROMPT,
    description: '石楠的会话摘要规则，负责压缩单个会话块。',
  },
  heather_stage_summary: {
    key: 'heather_stage_summary',
    label: 'Heather Stage Summary Profile',
    marker: '<Stage_Summary_Task>',
    content: HEATHER_STAGE_SUMMARY_PROMPT,
    description: '石楠的阶段摘要规则，负责把较远历史整理成阶段故事。',
  },
  heather_stage_fusion: {
    key: 'heather_stage_fusion',
    label: 'Heather Stage Fusion Profile',
    marker: '<Stage_Fusion_Task>',
    content: HEATHER_STAGE_FUSION_PROMPT,
    description: '石楠的阶段融合规则，负责合并旧阶段摘要并降低重复。',
  },
  hyacinth_profile: {
    key: 'hyacinth_profile',
    label: 'Hyacinth Battle Narration Profile',
    marker: '<Battle_Rules>',
    content: HYACINTH_PROFILE_PROMPT,
    description: '风信子的战斗叙事规则，当前作为战斗链路的预留 profile。',
  },
  iris_character_blueprint: {
    key: 'iris_character_blueprint',
    label: 'Iris Character Blueprint Profile',
    marker: '<Character_Blueprint_Rules>',
    content: IRIS_CHARACTER_BLUEPRINT_PROMPT,
    description: '鸢尾的人物蓝图规则，负责把描述一次成型为完整人物设定。',
  },
  ivy_protocol: {
    key: 'ivy_protocol',
    label: 'Ivy Choice Protocol',
    marker: '<Ivy_Choice_Protocol>',
    content: IVY_TOOL_CALL_PROTOCOL_PROMPT,
    description: '常春藤的结构化选项协议，负责在对话后产出可点击选项与稳定动作。',
  },
  lily_character_presence: {
    key: 'lily_character_presence',
    label: 'Lily Character Presence Profile',
    marker: '<Character_Presence_Design_Rules>',
    content: LILY_CHARACTER_PRESENCE_PROMPT,
    description: '百合的世界存在规则，负责角色在世界中的住处、活动与存在方式。',
  },
  verbena_site_content: {
    key: 'verbena_site_content',
    label: 'Verbena Site Content Profile',
    marker: '<Site_Content_Generation_Task>',
    content: VERBENA_SITE_CONTENT_PROMPT,
    description: '风铃草的据点内容规则，负责据点角色生成项、技能草案与掉落物品设计。',
  },
};

const GARDEN_FLOWER_TASK_PROFILES: Record<GardenFlowerTask, GardenFlowerTaskProfile> = {
  character_blueprint_design: {
    task: 'character_blueprint_design',
    flowerId: 'iris',
    flowerName: '鸢尾',
    profileName: 'IrisCharacterBlueprintProfile',
    responsibility: '完整人物设定蓝图成型',
    contextStrategy: 'dedicated_generator_runtime',
    blockKeys: ['iris_character_blueprint'],
    detectMarkers: ['<Character_Blueprint_Rules>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: ['aster', 'lily'],
  },
  character_combat_design: {
    task: 'character_combat_design',
    flowerId: 'aster',
    flowerName: '紫苑',
    profileName: 'AsterCharacterCombatProfile',
    responsibility: '人物战斗定位与技能表达设计',
    contextStrategy: 'dedicated_generator_runtime',
    blockKeys: ['aster_character_combat'],
    detectMarkers: ['<Character_Combat_Design_Rules>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: [],
  },
  quest_offer_generation: {
    task: 'quest_offer_generation',
    flowerId: 'camellia',
    flowerName: '山茶',
    profileName: 'CamelliaQuestOfferProfile',
    responsibility: '对话任务提议结构化生成',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['camellia_quest_offer'],
    detectMarkers: ['<Quest_Offer_Generation_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: [],
  },
  semantic_quest_judgement: {
    task: 'semantic_quest_judgement',
    flowerId: 'camellia',
    flowerName: '山茶',
    profileName: 'CamelliaSemanticQuestJudgeProfile',
    responsibility: '语义任务交付裁定与状态判断',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['camellia_semantic_quest_judge'],
    detectMarkers: ['<Semantic_Quest_Judgement_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: [],
  },
  character_conversation: {
    task: 'character_conversation',
    flowerId: 'freesia',
    flowerName: '小苍兰',
    profileName: 'FreesiaPromptProfile',
    responsibility: '人物会话与局部场景叙事',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['freesia_profile'],
    detectMarkers: ['<Conversation_Rules>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: ['ivy'],
  },
  character_presence_design: {
    task: 'character_presence_design',
    flowerId: 'lily',
    flowerName: '百合',
    profileName: 'LilyCharacterPresenceProfile',
    responsibility: '人物世界存在与调度方式设计',
    contextStrategy: 'dedicated_generator_runtime',
    blockKeys: ['lily_character_presence'],
    detectMarkers: ['<Character_Presence_Design_Rules>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: [],
  },
  site_shell_generation: {
    task: 'site_shell_generation',
    flowerId: 'dahlia',
    flowerName: '桔梗',
    profileName: 'DahliaSiteStructureProfile',
    responsibility: '据点本体信息生成',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['dahlia_site_structure'],
    detectMarkers: ['<Site_Architecture_Generation_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: ['verbena'],
  },
  site_rooms_generation: {
    task: 'site_rooms_generation',
    flowerId: 'dahlia',
    flowerName: '桔梗',
    profileName: 'DahliaSiteStructureProfile',
    responsibility: '据点房间蓝图与探索节奏生成',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['dahlia_site_structure'],
    detectMarkers: ['<Site_Architecture_Generation_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: ['verbena'],
  },
  site_content_generation: {
    task: 'site_content_generation',
    flowerId: 'verbena',
    flowerName: '风铃草',
    profileName: 'VerbenaSiteContentProfile',
    responsibility: '据点角色生成项与物品草案填充',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['verbena_site_content'],
    detectMarkers: ['<Site_Content_Generation_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: [],
  },
  story_history_session_summary: {
    task: 'story_history_session_summary',
    flowerId: 'heather',
    flowerName: '石楠',
    profileName: 'HeatherSessionSummaryProfile',
    responsibility: '会话块摘要与长期记忆整理',
    contextStrategy: 'history_summary_runtime',
    blockKeys: ['heather_session_summary'],
    detectMarkers: ['<Session_Summary_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: null,
    promptRole: 'system',
    corePromptMode: 'inline_with_core',
    allowedCollaborationTargets: [],
  },
  story_history_stage_fusion: {
    task: 'story_history_stage_fusion',
    flowerId: 'heather',
    flowerName: '石楠',
    profileName: 'HeatherStageFusionProfile',
    responsibility: '阶段摘要融合与远期历史去冗余',
    contextStrategy: 'history_summary_runtime',
    blockKeys: ['heather_stage_fusion'],
    detectMarkers: ['<Stage_Fusion_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: null,
    promptRole: 'system',
    corePromptMode: 'inline_with_core',
    allowedCollaborationTargets: [],
  },
  story_history_stage_summary: {
    task: 'story_history_stage_summary',
    flowerId: 'heather',
    flowerName: '石楠',
    profileName: 'HeatherStageSummaryProfile',
    responsibility: '阶段历史摘要与故事化整理',
    contextStrategy: 'history_summary_runtime',
    blockKeys: ['heather_stage_summary'],
    detectMarkers: ['<Stage_Summary_Task>'],
    isPrimaryFlower: true,
    markerIdentifier: null,
    promptRole: 'system',
    corePromptMode: 'inline_with_core',
    allowedCollaborationTargets: [],
  },
  battle_narration: {
    task: 'battle_narration',
    flowerId: 'hyacinth',
    flowerName: '风信子',
    profileName: 'HyacinthBattleNarrationProfile',
    responsibility: '战斗日志转玩家可见叙事',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['hyacinth_profile'],
    detectMarkers: ['<Battle_Rules>'],
    isPrimaryFlower: true,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'preset_marker',
    allowedCollaborationTargets: ['ivy'],
  },
  chat_interaction_protocol: {
    task: 'chat_interaction_protocol',
    flowerId: 'ivy',
    flowerName: '常春藤',
    profileName: 'IvyChoiceProfile',
    responsibility: '结构化选项协议与动作桥接',
    contextStrategy: 'shared_conversation_runtime',
    blockKeys: ['ivy_protocol'],
    detectMarkers: ['<Ivy_Choice_Protocol>'],
    isPrimaryFlower: false,
    markerIdentifier: 'gardenFlowerPrompt',
    promptRole: 'system',
    corePromptMode: 'none',
    allowedCollaborationTargets: [],
  },
};

function cloneTaskProfile(profile: GardenFlowerTaskProfile): GardenFlowerTaskProfile {
  return {
    ...profile,
    contextStrategy: profile.contextStrategy,
    blockKeys: [...profile.blockKeys],
    detectMarkers: [...profile.detectMarkers],
    allowedCollaborationTargets: [...profile.allowedCollaborationTargets],
  };
}

function resolveBlockProfile(key: GardenPromptBlockKey): GardenPromptBlockProfile {
  const profile = GARDEN_PROMPT_BLOCK_PROFILES[key];
  return {
    ...profile,
    content: profile.content.trim(),
  };
}

export class GardenFlowerProfileRegistry {
  static getCoreBlockProfile(): GardenPromptBlockProfile {
    return resolveBlockProfile('garden_core');
  }

  static getBlockProfile(key: GardenPromptBlockKey): GardenPromptBlockProfile {
    return resolveBlockProfile(key);
  }

  static getTaskProfile(task: GardenFlowerTask): GardenFlowerTaskProfile {
    return cloneTaskProfile(GARDEN_FLOWER_TASK_PROFILES[task]);
  }

  static listTaskProfiles(): GardenFlowerTaskProfile[] {
    return (Object.keys(GARDEN_FLOWER_TASK_PROFILES) as GardenFlowerTask[]).map(task => this.getTaskProfile(task));
  }

  static listTasksByContextStrategy(strategy: GardenFlowerContextStrategy): GardenFlowerTaskProfile[] {
    return this.listTaskProfiles().filter(profile => profile.contextStrategy === strategy);
  }

  static usesSharedConversationContext(task: GardenFlowerTask): boolean {
    return this.getTaskProfile(task).contextStrategy === 'shared_conversation_runtime';
  }

  static resolveTaskBlocks(task: GardenFlowerTask): GardenFlowerPromptBlock[] {
    const profile = this.getTaskProfile(task);

    return profile.blockKeys
      .map(key => this.getBlockProfile(key))
      .map(block => ({
        key: block.key,
        label: block.label,
        marker: block.marker,
        content: block.content,
      }))
      .filter(block => block.content.length > 0);
  }

  static findTaskProfileByMarkers(
    contents: readonly string[],
    options: { includeProtocolOnly?: boolean } = {},
  ): GardenFlowerTaskProfile | null {
    if (contents.length === 0) {
      return null;
    }

    const includeProtocolOnly = options.includeProtocolOnly ?? false;

    for (const profile of this.listTaskProfiles()) {
      if (!includeProtocolOnly && !profile.isPrimaryFlower) {
        continue;
      }

      const matched = profile.detectMarkers.every(marker => contents.some(content => content.includes(marker)));
      if (matched) {
        return profile;
      }
    }

    return null;
  }
}
